import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  addLineToShopifyCart,
  createShopifyCart,
  fetchShopifyCart,
  removeLineFromShopifyCart,
  updateShopifyCartLine,
} from "@/lib/shopify";

export interface CartStoreItem {
  productId: string;
  title: string;
  description: string;
  tag: string;
  handle: string;
  imageUrl: string | null;
  imageAlt: string | null;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  lineId: string | null;
}

interface CartStore {
  items: CartStoreItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  addItem: (item: Omit<CartStoreItem, "lineId" | "quantity"> & { quantity?: number }) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  removeItem: (productId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,

      addItem: async (item) => {
        const quantity = item.quantity ?? 1;
        const { cartId, clearCart } = get();
        const existingItem = get().items.find((entry) => entry.variantId === item.variantId);

        set({ isLoading: true });

        try {
          if (!cartId) {
            const result = await createShopifyCart(item.variantId, quantity);
            if (!result) return;

            set({
              cartId: result.cartId,
              checkoutUrl: result.checkoutUrl,
              items: [{ ...item, quantity, lineId: result.lineId }],
            });
            return;
          }

          if (existingItem?.lineId) {
            const nextQuantity = existingItem.quantity + quantity;
            const result = await updateShopifyCartLine(cartId, existingItem.lineId, nextQuantity);

            if (result.cartNotFound) {
              clearCart();
              return;
            }

            if (!result.success) return;

            set({
              checkoutUrl: result.checkoutUrl,
              items: get().items.map((entry) =>
                entry.variantId === item.variantId ? { ...entry, quantity: nextQuantity } : entry,
              ),
            });
            return;
          }

          const result = await addLineToShopifyCart(cartId, item.variantId, quantity);

          if (result.cartNotFound) {
            clearCart();
            return;
          }

          if (!result.success) return;

          set({
            checkoutUrl: result.checkoutUrl,
            items: [...get().items, { ...item, quantity, lineId: result.lineId }],
          });
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (productId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(productId);
          return;
        }

        const { cartId, clearCart } = get();
        const item = get().items.find((entry) => entry.productId === productId);
        if (!cartId || !item?.lineId) return;

        set({ isLoading: true });

        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, quantity);

          if (result.cartNotFound) {
            clearCart();
            return;
          }

          if (!result.success) return;

          set({
            checkoutUrl: result.checkoutUrl,
            items: get().items.map((entry) =>
              entry.productId === productId ? { ...entry, quantity } : entry,
            ),
          });
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (productId) => {
        const { cartId, clearCart } = get();
        const item = get().items.find((entry) => entry.productId === productId);
        if (!cartId || !item?.lineId) return;

        set({ isLoading: true });

        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);

          if (result.cartNotFound) {
            clearCart();
            return;
          }

          if (!result.success) return;

          const nextItems = get().items.filter((entry) => entry.productId !== productId);
          if (nextItems.length === 0) {
            clearCart();
            return;
          }

          set({
            checkoutUrl: result.checkoutUrl,
            items: nextItems,
          });
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;

        set({ isSyncing: true });

        try {
          const cart = await fetchShopifyCart(cartId);
          if (!cart || cart.totalQuantity === 0) {
            clearCart();
            return;
          }

          set({ checkoutUrl: cart.checkoutUrl });
        } finally {
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: "shopify-cart",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
        cartId: state.cartId,
        checkoutUrl: state.checkoutUrl,
      }),
    },
  ),
);
